import { useCallback, useEffect, useRef, useState } from "react";
import { Check, CornerDownLeft, Hexagon, MousePointer2, Save, Square, Trash2, Wand2, X } from "lucide-react";
import { api, tokenUrl } from "@/lib/api";
import { t } from "@/lib/i18n";
import { detectAreaAt, loadPlanBitmap, type WandFailure } from "@/lib/wand";
import {
  parsePolygonGeometry,
  parseRectGeometry,
  type FacilityMap,
  type FacilityMarker,
  type GenericResponse,
} from "@/lib/types";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { cn } from "@/lib/utils";

type MarkerEditorProps = {
  map: FacilityMap;
  onClose: () => void;
  onSaved: () => void;
};

type DraftMarker = Omit<FacilityMarker, "id"> & { id: number | null };
type Tool = "point" | "rect" | "polygon" | "wand";
type RectDraft = { startX: number; startY: number; curX: number; curY: number };
type PolyDraft = { points: [number, number][] };

type DragState = {
  idx: number;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  /** Bei Polygon: alle ursprünglichen Punkte, damit die Form als ganzes verschoben werden kann. */
  originalPoints?: [number, number][];
  /** Hat sich die Position genug bewegt, dass wir es als Drag und nicht als Click werten? */
  moved: boolean;
};

const POINT_DEFAULTS = {
  client_id: null,
  service: null,
  host: null,
  label: null,
  stale_after_minutes: 5,
  shape: "point" as const,
  geometry: null,
};

const MIN_RECT_PCT = 1; // Klicks unter 1×1% werden als Mis-Click verworfen
const POLY_CLOSE_THRESHOLD_PCT = 2.0; // Abstand zum Start-Punkt, der "schließen" auslöst
const DRAG_THRESHOLD_PX = 4; // Bewegung ab der wir Click → Drag promoten

export function MarkerEditor({ map, onClose, onSaved }: MarkerEditorProps) {
  const [markers, setMarkers] = useState<DraftMarker[]>([]);
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [tool, setTool] = useState<Tool>("point");
  const [rectDraft, setRectDraft] = useState<RectDraft | null>(null);
  const [polyDraft, setPolyDraft] = useState<PolyDraft | null>(null);
  const [cursorPct, setCursorPct] = useState<{ x: number; y: number } | null>(null);
  const [wandPreview, setWandPreview] = useState<[number, number][] | null>(null);
  const [wandBusy, setWandBusy] = useState(false);
  const dragRef = useRef<DragState | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // Analyse-Canvas für den Zauberstab — einmal pro Planbild gerastert.
  const wandCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    wandCanvasRef.current = null;
  }, [map.id, map.image_path]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await api.get<{ map: FacilityMap; markers: FacilityMarker[] }>(`/api/maps/${map.id}`);
      setMarkers(detail.markers.map((m) => ({ ...m, id: m.id })));
      setDeleteIds([]);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, [map.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Tool-Wechsel resetet laufende Drafts. Sonst hätten wir eine halbgezeichnete
  // Form die nirgendwo mehr eindeutig zuzuordnen ist.
  useEffect(() => {
    setRectDraft(null);
    setPolyDraft(null);
    setCursorPct(null);
    setWandPreview(null);
  }, [tool]);

  // Escape: laufende Drafts abbrechen
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setRectDraft(null);
      setPolyDraft(null);
      setCursorPct(null);
      setWandPreview(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Koordinaten-Helper ────────────────────────────────────────────
  const pctFromClient = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const img = imgRef.current;
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    return {
      x: clamp01x100(((clientX - rect.left) / rect.width) * 100),
      y: clamp01x100(((clientY - rect.top) / rect.height) * 100),
    };
  };

  // ── Tool: Punkt ───────────────────────────────────────────────────
  const placePoint = (x: number, y: number) => {
    const newMarker: DraftMarker = {
      ...POINT_DEFAULTS,
      id: null,
      map_id: map.id,
      x_pct: round2(x),
      y_pct: round2(y),
    };
    setMarkers((cur) => [...cur, newMarker]);
    setSelectedIdx(markers.length);
  };

  // ── Tool: Rechteck ────────────────────────────────────────────────
  const finishRectDraft = () => {
    const d = rectDraft;
    setRectDraft(null);
    if (!d) return;
    const x = Math.min(d.startX, d.curX);
    const y = Math.min(d.startY, d.curY);
    const w = Math.abs(d.curX - d.startX);
    const h = Math.abs(d.curY - d.startY);
    if (w < MIN_RECT_PCT || h < MIN_RECT_PCT) return;
    const newMarker: DraftMarker = {
      ...POINT_DEFAULTS,
      id: null,
      map_id: map.id,
      x_pct: round2(x),
      y_pct: round2(y),
      shape: "rect",
      geometry: JSON.stringify({ w: round2(w), h: round2(h) }),
    };
    setMarkers((cur) => [...cur, newMarker]);
    setSelectedIdx(markers.length);
  };

  // ── Tool: Polygon ─────────────────────────────────────────────────
  const addPolygonPoint = (x: number, y: number) => {
    const rx = round2(x);
    const ry = round2(y);
    setPolyDraft((cur) => {
      if (!cur) return { points: [[rx, ry]] };
      // Klick nahe Startpunkt → schließen, sobald ≥ 3 Punkte gesetzt sind
      const [sx, sy] = cur.points[0];
      if (cur.points.length >= 3 && Math.hypot(x - sx, y - sy) < POLY_CLOSE_THRESHOLD_PCT) {
        commitPolygon(cur.points);
        return null;
      }
      return { points: [...cur.points, [rx, ry]] };
    });
  };

  const commitPolygon = (points: [number, number][]) => {
    if (points.length < 3) return;
    const newMarker: DraftMarker = {
      ...POINT_DEFAULTS,
      id: null,
      map_id: map.id,
      x_pct: points[0][0],
      y_pct: points[0][1],
      shape: "polygon",
      geometry: JSON.stringify({ points }),
    };
    setMarkers((cur) => [...cur, newMarker]);
    setSelectedIdx(markers.length);
    setCursorPct(null);
  };

  const cancelPolygonDraft = () => {
    setPolyDraft(null);
    setCursorPct(null);
  };

  // ── Tool: Zauberstab (Flächenerkennung) ───────────────────────────
  const runWand = async (x: number, y: number) => {
    if (wandBusy) return;
    setWandBusy(true);
    setFeedback(null);
    try {
      if (!wandCanvasRef.current) {
        wandCanvasRef.current = await loadPlanBitmap(
          tokenUrl(`/api/maps/${map.id}/image?v=${encodeURIComponent(map.image_path)}`),
          imgRef.current,
        );
      }
      const res = detectAreaAt(wandCanvasRef.current, x, y);
      if (!res.ok) {
        setWandPreview(null);
        setFeedback({ kind: "error", text: wandFailureText(res.reason) });
        return;
      }
      setWandPreview(res.points);
    } catch (e) {
      setWandPreview(null);
      setFeedback({
        kind: "error",
        text: e instanceof Error ? e.message : t("Flächenerkennung fehlgeschlagen"),
      });
    } finally {
      setWandBusy(false);
    }
  };

  const acceptWand = () => {
    if (!wandPreview) return;
    commitPolygon(wandPreview);
    setWandPreview(null);
  };

  // ── Image-Event-Handler ───────────────────────────────────────────
  const onImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const p = pctFromClient(e.clientX, e.clientY);
    if (!p) return;
    if (tool === "point") placePoint(p.x, p.y);
    if (tool === "polygon") addPolygonPoint(p.x, p.y);
    if (tool === "wand") void runWand(p.x, p.y);
  };

  const onImageMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (tool !== "rect") return;
    e.preventDefault();
    const p = pctFromClient(e.clientX, e.clientY);
    if (!p) return;
    setRectDraft({ startX: p.x, startY: p.y, curX: p.x, curY: p.y });
  };

  const onStageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const p = pctFromClient(e.clientX, e.clientY);
    if (!p) return;
    if (rectDraft) setRectDraft({ ...rectDraft, curX: p.x, curY: p.y });
    if (tool === "polygon" && polyDraft) setCursorPct(p);
  };

  // ── Drag bestehender Marker (Pointer Capture) ─────────────────────
  const onMarkerPointerDown = (e: React.PointerEvent<Element>, idx: number) => {
    if (rectDraft || polyDraft) return; // mitten in einem Draft → nicht draggen
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget;
    try {
      target.setPointerCapture(e.pointerId);
    } catch {
      // ältere Browser / SVG-Quirks → einfach weitermachen
    }
    setSelectedIdx(idx);
    const m = markers[idx];
    const originalPoints =
      m.shape === "polygon" ? parsePolygonGeometry(m.geometry)?.points : undefined;
    dragRef.current = {
      idx,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: m.x_pct,
      startY: m.y_pct,
      originalPoints,
      moved: false,
    };
  };

  const onMarkerPointerMove = (e: React.PointerEvent<Element>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dxPx = e.clientX - drag.startClientX;
    const dyPx = e.clientY - drag.startClientY;
    if (!drag.moved && Math.hypot(dxPx, dyPx) < DRAG_THRESHOLD_PX) return; // noch ein Click

    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const dxPct = (dxPx / rect.width) * 100;
    const dyPct = (dyPx / rect.height) * 100;
    drag.moved = true;

    setMarkers((cur) =>
      cur.map((m, i) => {
        if (i !== drag.idx) return m;
        const newX = clamp01x100(drag.startX + dxPct);
        const newY = clamp01x100(drag.startY + dyPct);
        if (m.shape === "polygon" && drag.originalPoints) {
          const dx = newX - drag.startX;
          const dy = newY - drag.startY;
          const newPoints = drag.originalPoints.map(
            ([x, y]) => [clamp01x100(x + dx), clamp01x100(y + dy)] as [number, number],
          );
          return {
            ...m,
            x_pct: round2(newX),
            y_pct: round2(newY),
            geometry: JSON.stringify({ points: newPoints.map(([x, y]) => [round2(x), round2(y)]) }),
          };
        }
        return { ...m, x_pct: round2(newX), y_pct: round2(newY) };
      }),
    );
  };

  const onMarkerPointerUp = (e: React.PointerEvent<Element>) => {
    const drag = dragRef.current;
    if (!drag) return;
    try {
      e.currentTarget.releasePointerCapture(drag.pointerId);
    } catch {
      // ignore
    }
    dragRef.current = null;
  };

  const updateMarker = (idx: number, patch: Partial<DraftMarker>) => {
    setMarkers((cur) => cur.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const updateRectGeometry = (idx: number, patch: Partial<{ w: number; h: number }>) => {
    setMarkers((cur) =>
      cur.map((m, i) => {
        if (i !== idx || m.shape !== "rect") return m;
        const cur_geo = parseRectGeometry(m.geometry) ?? { w: 5, h: 5 };
        const next = { ...cur_geo, ...patch };
        return { ...m, geometry: JSON.stringify({ w: round2(next.w), h: round2(next.h) }) };
      }),
    );
  };

  const removeMarker = (idx: number) => {
    setMarkers((cur) => {
      const m = cur[idx];
      if (m.id != null) setDeleteIds((d) => [...d, m.id as number]);
      return cur.filter((_, i) => i !== idx);
    });
    setSelectedIdx(null);
  };

  const onSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const body = {
        markers: markers.map((m) => ({
          id: m.id ?? -1,
          map_id: map.id,
          client_id: m.client_id,
          service: m.service,
          host: m.host,
          x_pct: m.x_pct,
          y_pct: m.y_pct,
          label: m.label,
          stale_after_minutes: m.stale_after_minutes,
          shape: m.shape,
          geometry: m.geometry,
        })),
        delete_ids: deleteIds,
      };
      await api.put<GenericResponse>(`/api/maps/${map.id}/markers`, body);
      setFeedback({ kind: "success", text: t("Marker gespeichert.") });
      onSaved();
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  const selected = selectedIdx != null ? markers[selectedIdx] : null;
  const cursorNearPolyStart =
    tool === "polygon" &&
    polyDraft != null &&
    polyDraft.points.length >= 3 &&
    cursorPct != null &&
    Math.hypot(cursorPct.x - polyDraft.points[0][0], cursorPct.y - polyDraft.points[0][1]) <
      POLY_CLOSE_THRESHOLD_PCT;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-medium">{t("Marker-Editor")}</h3>
          <p className="text-xs text-muted-foreground">
            {wandBusy ? t("Erkenne Fläche…") : hintFor(tool, polyDraft, markers.length)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={saving || loading}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
          >
            <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Speichern")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" /> {t("Schließen")}
          </button>
        </div>
      </div>

      <FormMessage feedback={feedback} />

      {/* Tool-Switcher */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex overflow-hidden rounded-md border border-border bg-card text-xs">
          <ToolButton active={tool === "point"} onClick={() => setTool("point")} Icon={MousePointer2}>
            {t("Punkt")}
          </ToolButton>
          <ToolButton active={tool === "rect"} onClick={() => setTool("rect")} Icon={Square}>
            {t("Rechteck")}
          </ToolButton>
          <ToolButton active={tool === "polygon"} onClick={() => setTool("polygon")} Icon={Hexagon}>
            {t("Polygon")}
          </ToolButton>
          <ToolButton active={tool === "wand"} onClick={() => setTool("wand")} Icon={Wand2}>
            {t("Fläche")}
          </ToolButton>
        </div>

        {/* Zauberstab-Aktionen */}
        {tool === "wand" && wandPreview ? (
          <>
            <button
              type="button"
              onClick={acceptWand}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
            >
              <Check className="h-3 w-3" /> {t("Fläche übernehmen")}
            </button>
            <button
              type="button"
              onClick={() => setWandPreview(null)}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
            >
              <X className="h-3 w-3" /> {t("Verwerfen (Esc)")}
            </button>
          </>
        ) : null}

        {/* Polygon-Aktionen */}
        {tool === "polygon" && polyDraft && polyDraft.points.length >= 3 ? (
          <button
            type="button"
            onClick={() => commitPolygon(polyDraft.points)}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
          >
            <CornerDownLeft className="h-3 w-3" /> {t("Polygon schließen")}
          </button>
        ) : null}
        {tool === "polygon" && polyDraft && polyDraft.points.length > 0 ? (
          <button
            type="button"
            onClick={cancelPolygonDraft}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
          >
            <X className="h-3 w-3" /> {t("Verwerfen (Esc)")}
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">{t("Lade…")}</div>
      ) : (
        <>
          {/* Stage bewusst block + img w-full: SVG-Pläne ohne width/height-
              Attribute (nur viewBox) haben keine intrinsische Größe und
              kollabieren sonst in Safari zu 0×0 (shrink-to-fit-Zyklus). */}
          <div
            className="relative select-none overflow-hidden rounded-md border border-border bg-muted/40"
            onMouseMove={onStageMouseMove}
            onMouseUp={finishRectDraft}
            onMouseLeave={() => rectDraft && setRectDraft(null)}
          >
            <img
              ref={imgRef}
              src={tokenUrl(`/api/maps/${map.id}/image?v=${encodeURIComponent(map.image_path)}`)}
              alt={map.name}
              onClick={onImageClick}
              onMouseDown={onImageMouseDown}
              className="block w-full cursor-crosshair select-none"
              draggable={false}
            />

            {/* Existierende Marker als HTML-Layer (Point + Rect + Polygon-Anchor) */}
            {markers.map((m, i) =>
              renderMarkerHandle(m, i, i === selectedIdx, {
                onPointerDown: onMarkerPointerDown,
                onPointerMove: onMarkerPointerMove,
                onPointerUp: onMarkerPointerUp,
              }),
            )}

            {/* Polygon-Layer als SVG (vorhandene Polygone + Draft) */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="pointer-events-none absolute inset-0 h-full w-full"
            >
              {markers.map((m, i) => {
                if (m.shape !== "polygon") return null;
                const geo = parsePolygonGeometry(m.geometry);
                if (!geo) return null;
                const isSel = i === selectedIdx;
                return (
                  <polygon
                    key={`poly-${i}`}
                    points={geo.points.map((p) => p.join(",")).join(" ")}
                    fill="rgb(20 184 166 / 0.18)"
                    stroke={isSel ? "rgb(13 148 136)" : "rgb(20 184 166)"}
                    strokeWidth={isSel ? "0.7" : "0.45"}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: "auto", cursor: "move" }}
                    onPointerDown={(e) => onMarkerPointerDown(e, i)}
                    onPointerMove={onMarkerPointerMove}
                    onPointerUp={onMarkerPointerUp}
                  />
                );
              })}

              {/* Zauberstab-Vorschau: erkannte Kontur */}
              {wandPreview ? (
                <polygon
                  points={wandPreview.map((p) => p.join(",")).join(" ")}
                  fill="rgb(34 197 94 / 0.18)"
                  stroke="hsl(var(--success))"
                  strokeWidth="0.6"
                  strokeDasharray="1,0.7"
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}

              {/* Polygon-Draft (Linien + Punkte) */}
              {polyDraft ? (
                <>
                  {/* gesetzte Strecken zwischen den Punkten */}
                  {polyDraft.points.slice(0, -1).map((p, i) => {
                    const next = polyDraft.points[i + 1];
                    return (
                      <line
                        key={`pd-${i}`}
                        x1={p[0]}
                        y1={p[1]}
                        x2={next[0]}
                        y2={next[1]}
                        stroke="hsl(var(--primary))"
                        strokeWidth="0.5"
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                  {/* Preview-Linie vom letzten Punkt zum Cursor */}
                  {cursorPct ? (
                    <line
                      x1={polyDraft.points[polyDraft.points.length - 1][0]}
                      y1={polyDraft.points[polyDraft.points.length - 1][1]}
                      x2={cursorPct.x}
                      y2={cursorPct.y}
                      stroke="hsl(var(--primary))"
                      strokeWidth="0.4"
                      strokeDasharray="0.6,0.6"
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : null}
                  {/* Schließen-Linie zum Start-Punkt, wenn Cursor nah genug ist */}
                  {cursorNearPolyStart && cursorPct ? (
                    <line
                      x1={cursorPct.x}
                      y1={cursorPct.y}
                      x2={polyDraft.points[0][0]}
                      y2={polyDraft.points[0][1]}
                      stroke="hsl(var(--success))"
                      strokeWidth="0.5"
                      strokeDasharray="0.6,0.6"
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : null}
                  {/* Punkt-Marker */}
                  {polyDraft.points.map((p, i) => (
                    <circle
                      key={`pp-${i}`}
                      cx={p[0]}
                      cy={p[1]}
                      r={i === 0 && cursorNearPolyStart ? "1.0" : "0.7"}
                      fill={i === 0 && cursorNearPolyStart ? "hsl(var(--success))" : "hsl(var(--primary))"}
                      stroke="hsl(var(--card))"
                      strokeWidth="0.25"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </>
              ) : null}
            </svg>

            {/* Rechteck-Draft */}
            {rectDraft ? (
              <div
                className="pointer-events-none absolute border-2 border-dashed border-primary bg-primary/15"
                style={{
                  left: `${Math.min(rectDraft.startX, rectDraft.curX)}%`,
                  top: `${Math.min(rectDraft.startY, rectDraft.curY)}%`,
                  width: `${Math.abs(rectDraft.curX - rectDraft.startX)}%`,
                  height: `${Math.abs(rectDraft.curY - rectDraft.startY)}%`,
                }}
              />
            ) : null}
          </div>

          {selected != null ? (
            <div className="mt-4 grid grid-cols-1 gap-3 rounded-md border border-border bg-muted/30 p-3 md:grid-cols-2">
              <MarkerField
                label={t("Label")}
                value={selected.label ?? ""}
                onChange={(v) => updateMarker(selectedIdx!, { label: v || null })}
                placeholder={t("z.B. Maschine 4")}
              />
              <MarkerField
                label={t("Client-ID")}
                value={selected.client_id ?? ""}
                onChange={(v) => updateMarker(selectedIdx!, { client_id: v || null })}
                placeholder={t("z.B. sensor-a4 (oder leer)")}
              />
              <MarkerField
                label={t("Service")}
                value={selected.service ?? ""}
                onChange={(v) => updateMarker(selectedIdx!, { service: v || null })}
                placeholder={t("optional, * als Wildcard")}
              />
              <MarkerField
                label={t("Host")}
                value={selected.host ?? ""}
                onChange={(v) => updateMarker(selectedIdx!, { host: v || null })}
                placeholder={t("optional")}
              />
              <MarkerField
                label={t("Stale nach (Minuten)")}
                value={String(selected.stale_after_minutes)}
                onChange={(v) =>
                  updateMarker(selectedIdx!, { stale_after_minutes: Math.max(1, parseInt(v, 10) || 1) })
                }
                type="number"
              />

              <ShapeInfo
                marker={selected}
                onWidth={(w) => updateRectGeometry(selectedIdx!, { w })}
                onHeight={(h) => updateRectGeometry(selectedIdx!, { h })}
              />

              <div className="flex items-end justify-between gap-2 md:col-span-2">
                <span className="text-[11px] text-muted-foreground">
                  {t("Position: {x}% / {y}%", { x: selected.x_pct.toFixed(1), y: selected.y_pct.toFixed(1) })}
                </span>
                <button
                  type="button"
                  onClick={() => removeMarker(selectedIdx!)}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {t("Marker löschen")}
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

type DragHandlers = {
  onPointerDown: (e: React.PointerEvent<Element>, idx: number) => void;
  onPointerMove: (e: React.PointerEvent<Element>) => void;
  onPointerUp: (e: React.PointerEvent<Element>) => void;
};

function renderMarkerHandle(m: DraftMarker, i: number, isSelected: boolean, h: DragHandlers) {
  if (m.shape === "rect") {
    const geo = parseRectGeometry(m.geometry);
    if (!geo) return null;
    return (
      <button
        key={i}
        type="button"
        onPointerDown={(e) => h.onPointerDown(e, i)}
        onPointerMove={h.onPointerMove}
        onPointerUp={h.onPointerUp}
        style={{
          left: `${m.x_pct}%`,
          top: `${m.y_pct}%`,
          width: `${geo.w}%`,
          height: `${geo.h}%`,
          touchAction: "none",
        }}
        title={m.label ?? m.client_id ?? t("Rechteck")}
        className={cn(
          "absolute cursor-move rounded border-2 transition-colors",
          isSelected
            ? "border-primary bg-primary/25"
            : "border-foreground/40 bg-foreground/10 hover:bg-foreground/15",
        )}
      />
    );
  }

  if (m.shape === "polygon") {
    // Anchor am ersten Punkt — die eigentliche Form-Drag-Logik hängt am
    // SVG-polygon weiter unten in der Komponente. Hier nur ein kleiner
    // klickbarer Indikator.
    return (
      <button
        key={i}
        type="button"
        onPointerDown={(e) => h.onPointerDown(e, i)}
        onPointerMove={h.onPointerMove}
        onPointerUp={h.onPointerUp}
        style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%`, touchAction: "none" }}
        title={m.label ?? m.client_id ?? t("Polygon")}
        className={cn(
          "absolute -translate-x-1/2 -translate-y-1/2 cursor-move rounded-sm border-2 shadow",
          isSelected ? "h-5 w-5 border-primary bg-primary/40" : "h-3.5 w-3.5 border-foreground/70 bg-card",
        )}
      />
    );
  }

  // point default
  return (
    <button
      key={i}
      type="button"
      onPointerDown={(e) => h.onPointerDown(e, i)}
      onPointerMove={h.onPointerMove}
      onPointerUp={h.onPointerUp}
      style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%`, touchAction: "none" }}
      title={m.label ?? m.client_id ?? t("Marker")}
      className={cn(
        "absolute -translate-x-1/2 -translate-y-1/2 cursor-move rounded-full border-2 shadow",
        isSelected
          ? "h-5 w-5 border-primary bg-primary/40"
          : "h-3.5 w-3.5 border-foreground/70 bg-card hover:scale-125",
      )}
    />
  );
}

function ShapeInfo({
  marker,
  onWidth,
  onHeight,
}: {
  marker: DraftMarker;
  onWidth: (w: number) => void;
  onHeight: (h: number) => void;
}) {
  if (marker.shape === "rect") {
    const geo = parseRectGeometry(marker.geometry) ?? { w: 5, h: 5 };
    return (
      <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-card p-2 text-xs md:col-span-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("Breite (%)")}
          </span>
          <input
            type="number"
            step="0.1"
            min="0.5"
            value={geo.w}
            onChange={(e) => onWidth(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
            className="h-8 rounded-md border border-border bg-background px-2 outline-none focus:border-primary"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("Höhe (%)")}
          </span>
          <input
            type="number"
            step="0.1"
            min="0.5"
            value={geo.h}
            onChange={(e) => onHeight(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
            className="h-8 rounded-md border border-border bg-background px-2 outline-none focus:border-primary"
          />
        </label>
      </div>
    );
  }
  if (marker.shape === "polygon") {
    const geo = parsePolygonGeometry(marker.geometry);
    const n = geo?.points.length ?? 0;
    return (
      <div className="rounded-md border border-border bg-card p-2 text-[11px] text-muted-foreground md:col-span-2">
        {t("Polygon mit {n} Eckpunkten. Form kann per Drag verschoben werden. Einzelne Punkte umzubiegen ist im Editor nicht vorgesehen — wer die Form ändern will, löscht das Polygon und zeichnet es neu.", { n })}
      </div>
    );
  }
  return null;
}

function ToolButton({
  active,
  onClick,
  Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  Icon: typeof MousePointer2;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors",
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

function MarkerField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: "text" | "number";
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />
    </label>
  );
}

function hintFor(tool: Tool, polyDraft: PolyDraft | null, count: number): string {
  if (tool === "point") {
    return count === 0
      ? t("Punkt-Tool: Klick irgendwo auf den Plan, um den ersten Marker zu setzen. Bestehende Marker per Drag verschieben.")
      : t("Punkt-Tool: Klick platziert einen neuen Pin. Bestehende per Drag verschieben.");
  }
  if (tool === "rect") {
    return t("Rechteck-Tool: Maustaste gedrückt halten und Bereich aufziehen. Bestehende Marker per Drag verschieben.");
  }
  if (tool === "wand") {
    return t("Flächen-Tool: Klick auf eine Maschinen-/Anlagenfläche — die Kontur wird automatisch erkannt und als Vorschau angezeigt.");
  }
  if (polyDraft && polyDraft.points.length === 0) {
    return t("Polygon-Tool: Erste Ecke setzen, dann weiterklicken.");
  }
  if (polyDraft && polyDraft.points.length < 3) {
    return t("Polygon-Tool: {n} von mindestens 3 Punkten — weiter klicken.", { n: polyDraft.points.length });
  }
  if (polyDraft) {
    return t("Polygon-Tool: Klick auf den Startpunkt (grün) oder Button rechts oben zum Schließen. Esc verwirft.");
  }
  return t("Polygon-Tool: Klick setzt die erste Ecke.");
}

function wandFailureText(reason: WandFailure): string {
  switch (reason) {
    case "too-large":
      return t("Erkannte Fläche ist zu groß — bitte direkt auf eine Maschine/Anlage klicken, nicht auf den Hallenboden.");
    case "too-small":
      return t("Keine brauchbare Fläche erkannt (zu klein). An anderer Stelle der Fläche klicken.");
    default:
      return t("Keine zusammenhängende Fläche am Klickpunkt erkannt.");
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function clamp01x100(n: number): number {
  return Math.max(0, Math.min(100, n));
}
